import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { GlassCard, ConfirmDelete, TIP_ETIKET } from "@/components/erp";
import { FormDialog } from "@/components/FormDialog";
import api, { errMsg } from "@/lib/api";

const TIP = { text: "Metin", number: "Sayı", select: "Seçim listesi", date: "Tarih", boolean: "Evet/Hayır", arac_listesi: "Uyumlu araç listesi" };

const CONFIG = {
  oznitelikler: {
    baslik: "Ürün kartında sektöre özel alanlar. Seçim listesi seçenekleri virgülle ayrılır.",
    bos: { ad: "", kod: "", tip: "text", secenekler: "", zorunlu: false, sira: 1, aktif: true },
    fields: [
      { k: "ad", label: "Alan adı *" }, { k: "kod", label: "Kod * (örn. renk)" },
      { k: "tip", label: "Tip", type: "select", options: Object.entries(TIP).map(([value, label]) => ({ value, label })) },
      { k: "sira", label: "Sıra", type: "number" },
      { k: "secenekler", label: "Seçenekler (virgülle)", wide: true },
      { k: "zorunlu", label: "Zorunlu", type: "switch" }, { k: "aktif", label: "Aktif", type: "switch" },
    ],
    cols: (o) => [o.ad, <span className="font-mono text-xs">{o.kod}</span>, TIP[o.tip], o.zorunlu ? "Zorunlu" : "—"],
    head: ["Alan", "Kod", "Tip", "Zorunlu"],
    toForm: (o) => ({ ...o, secenekler: (o.secenekler || []).join(", ") }),
    toApi: (f) => ({ ...f, secenekler: String(f.secenekler || "").split(",").map((s) => s.trim()).filter(Boolean) }),
  },
  ambalajlar: {
    baslik: "Ambalaj hiyerarşisi (tekli → paket → koli → palet). Çarpan: bir alt seviyeden kaç adet.",
    bos: { ad: "", kod: "", carpan: 1, sira: 1 },
    fields: [{ k: "ad", label: "Ad *" }, { k: "kod", label: "Kod *" }, { k: "carpan", label: "Varsayılan çarpan", type: "number" }, { k: "sira", label: "Sıra", type: "number" }],
    cols: (o) => [o.ad, <span className="font-mono text-xs">{o.kod}</span>, `× ${o.carpan}`, o.sira],
    head: ["Ad", "Kod", "Çarpan", "Sıra"],
    toForm: (o) => o, toApi: (f) => f,
  },
};

export const ListeYonetimi = ({ path }) => {
  const c = CONFIG[path];
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(async () => setList((await api.get(`/${path}`)).data), [path]);
  useEffect(() => { load(); }, [load]);

  const kaydet = async (f) => {
    const body = c.toApi(f);
    if (f.id) await api.put(`/${path}/${f.id}`, body); else await api.post(`/${path}`, body);
    toast.success("Kaydedildi"); load();
  };
  const sil = async (id) => {
    try { await api.delete(`/${path}/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <GlassCard className="p-6" data-testid={`liste-${path}`}>
      <div className="flex justify-between items-start gap-4 mb-4">
        <p className="text-sm text-muted-foreground">{c.baslik}</p>
        <button data-testid={`${path}-ekle`} onClick={() => setDlg({ ...c.bos, sira: list.length + 1 })} className="shrink-0 text-sm inline-flex items-center gap-1.5 text-[var(--g1)] hover:brightness-125"><Plus className="w-4 h-4" />Ekle</button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/5">{c.head.map((h) => <th key={h} className="py-3 pr-4">{h}</th>)}<th /></tr></thead>
        <tbody>
          {list.map((o) => (
            <tr key={o.id} className="border-b border-white/5" data-testid={`${path}-satir-${o.kod}`}>
              {c.cols(o).map((v, i) => <td key={i} className="py-3 pr-4">{v}</td>)}
              <td className="py-3 text-right whitespace-nowrap">
                <button data-testid={`${path}-duzenle-${o.kod}`} onClick={() => setDlg(c.toForm(o))} className="p-2 rounded-lg text-muted-foreground hover:text-white"><Pencil className="w-4 h-4" /></button>
                <ConfirmDelete testid={`${path}-sil-${o.kod}`} onConfirm={() => sil(o.id)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {dlg && <FormDialog prefix={path} title={dlg.id ? "Düzenle" : "Yeni kayıt"} fields={c.fields} initial={dlg} onSubmit={kaydet} onClose={() => setDlg(null)} />}
    </GlassCard>
  );
};

export { TIP_ETIKET };
